import { useEffect, useState } from 'react';
import {
  deficiencyEntriesApi,
  type EntrySeverity,
} from '../../api/deficiency-entries';

interface Props {
  open: boolean;
  /** Seed values pulled from the finding being saved. */
  seed: {
    title: string;
    description: string | null;
    severity: EntrySeverity;
    sectionName: string;
    componentName: string | null;
  };
  /**
   * Where this entry came from. `user_curated` is set when the inspector saves
   * an accepted/edited AI vision finding (issue #44) — these entries rank
   * above `ai_generated` defaults in future SuggestionsStrip lookups.
   */
  source?: 'user_library' | 'user_curated';
  onClose: () => void;
  onSaved: () => void;
}

const SEVERITY_OPTS: Array<{ value: EntrySeverity; label: string }> = [
  { value: 'safety_concern', label: 'Safety Concern' },
  { value: 'recommendation', label: 'Recommendation' },
  { value: null, label: 'Observation' },
];

export function SaveToLibrarySheet({ open, seed, source = 'user_library', onClose, onSaved }: Props) {
  const [title, setTitle] = useState(seed.title);
  const [description, setDescription] = useState(seed.description ?? '');
  const [severity, setSeverity] = useState<EntrySeverity>(seed.severity);
  const [sectionName, setSectionName] = useState(seed.sectionName);
  const [componentName, setComponentName] = useState(seed.componentName ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle(seed.title);
    setDescription(seed.description ?? '');
    setSeverity(seed.severity);
    setSectionName(seed.sectionName);
    setComponentName(seed.componentName ?? '');
    setError(null);
  }, [open, seed.title, seed.description, seed.severity, seed.sectionName, seed.componentName]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !sectionName.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      await deficiencyEntriesApi.create({
        title: title.trim(),
        description: description.trim() || null,
        severity,
        section_name: sectionName.trim(),
        component_name: componentName.trim() || null,
        source,
      });
      onSaved();
    } catch (err) {
      setError((err as Error).message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dl-sheet-backdrop" onClick={onClose}>
      <form
        className="dl-sheet"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="dl-sheet-head">
          <div className="dl-sheet-title">Save to library</div>
          <div className="dl-sheet-sub">
            {source === 'user_curated'
              ? 'Save your edited AI suggestion as a reusable template'
              : 'Reusable for future inspections'}
          </div>
        </div>

        <label className="dl-field">
          <span className="dl-field-label">Title</span>
          <input
            className="dl-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            required
          />
        </label>

        <label className="dl-field">
          <span className="dl-field-label">Description</span>
          <textarea
            className="dl-input dl-input--textarea"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <div className="dl-field">
          <span className="dl-field-label">Severity</span>
          <div className="dl-sev-group" role="radiogroup" aria-label="Severity">
            {SEVERITY_OPTS.map((opt) => {
              const on = severity === opt.value;
              const base =
                opt.value === 'safety_concern' ? 'dl-sev-pill dl-sev-pill--safety'
                : opt.value === 'recommendation' ? 'dl-sev-pill dl-sev-pill--rec'
                : 'dl-sev-pill dl-sev-pill--none';
              return (
                <button
                  key={String(opt.value)}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  className={base + (on ? ' dl-sev-pill--on' : '')}
                  onClick={() => setSeverity(opt.value)}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="dl-drawer-grid" style={{ gap: '12px 12px' }}>
          <label className="dl-field">
            <span className="dl-field-label">Section</span>
            <input
              className="dl-input"
              value={sectionName}
              onChange={(e) => setSectionName(e.target.value)}
              required
            />
          </label>
          <label className="dl-field">
            <span className="dl-field-label">Component (optional)</span>
            <input
              className="dl-input"
              value={componentName}
              onChange={(e) => setComponentName(e.target.value)}
            />
          </label>
        </div>

        {error && (
          <div style={{ fontSize: 12.5, color: 'var(--safety-fg)' }}>{error}</div>
        )}

        <div className="dl-sheet-actions">
          <button
            type="button"
            className="ts-btn ts-btn--secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="ts-btn ts-btn--primary"
            disabled={saving || !title.trim() || !sectionName.trim()}
          >
            {saving ? 'Saving…' : 'Save entry'}
          </button>
        </div>
      </form>
    </div>
  );
}
